import { Control, FieldValues } from 'react-hook-form'

import { Label } from '@/common/ui/label'
import { Switch } from '@/common/ui/switch'
import { FormControl, FormField, FormItem } from '@/common/ui/form'
import { Alert, AlertDescription } from '@/common/ui/alert'
import { PROVIDER_PAIRS } from '@/modules/promotion/components/GameProviderSelector'

type ProviderVersionSettingsProps<T extends FieldValues> = {
	control: Control<T>
	fieldPrefix?: string
}

export default function ProviderVersionSettings<T extends FieldValues>({
	control,
	fieldPrefix = '',
}: ProviderVersionSettingsProps<T>) {
	const getFieldName = (field: string) => {
		return fieldPrefix ? `${fieldPrefix}.${field}` : field
	}
	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-sm font-semibold sm:text-base">
					เวอร์ชันค่ายเกม (ปรับแตก / เวอร์ชั่นเริ่มต้น)
				</h3>
				<p className="text-xs text-muted-foreground sm:text-sm">
					กำหนดว่าผู้เล่นที่ใช้โปรโมชั่นนี้จะเล่นแบบปรับแตก หรือ
					เวอร์ชั่นเริ่มต้น
				</p>
			</div>

			<Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
				<AlertDescription className="space-y-2 text-xs text-blue-800 dark:text-blue-200">
					<p>
						<strong>ปรับแตก</strong> =
						ระบบค่ายเกมของทางเราที่สามารถตั้งค่าการปรับแตกได้ •{' '}
						<strong>เวอร์ชั่นเริ่มต้น</strong> = ค่ายเกมจากผู้ให้บริการ
						(ไม่สามารถปรับแตกได้) • ค่าเริ่มต้นเป็น ปรับแตก
					</p>
				</AlertDescription>
			</Alert>

			<Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950">
				<AlertDescription className="text-xs text-orange-800 dark:text-orange-200">
					<p className="font-semibold">
						⚠️ สำคัญ:
						ต้องตั้งค่าเวอร์ชั่นให้ตรงกับผู้ให้บริการเกมที่ร่วมในโปรโมชั่น
					</p>
				</AlertDescription>
			</Alert>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				<FormField
					control={control}
					name={getFieldName('pgHard') as any}
					render={({ field }) => (
						<FormItem>
							<div className="flex items-center justify-between space-x-4 rounded-lg border p-4">
								<div className="flex-1 space-y-1">
									<Label
										htmlFor="pgHard"
										className="cursor-pointer text-base font-semibold"
									>
										PG
									</Label>
									<p className="text-sm text-muted-foreground">
										{field.value ? (
											<span className="font-medium text-blue-600 dark:text-blue-400">
												ปรับแตก (PGHARD)
											</span>
										) : (
											<span className="font-medium text-green-600 dark:text-green-400">
												เวอร์ชั่นเริ่มต้น
											</span>
										)}
									</p>
								</div>
								<FormControl>
									<Switch
										id="pgHard"
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
							</div>
						</FormItem>
					)}
				/>

				<FormField
					control={control}
					name={getFieldName('jokerHard') as any}
					render={({ field }) => (
						<FormItem>
							<div className="flex items-center justify-between space-x-4 rounded-lg border p-4">
								<div className="flex-1 space-y-1">
									<Label
										htmlFor="jokerHard"
										className="cursor-pointer text-base font-semibold"
									>
										Joker
									</Label>
									<p className="text-sm text-muted-foreground">
										{field.value ? (
											<span className="font-medium text-blue-600 dark:text-blue-400">
												ปรับแตก (JOKERHARD)
											</span>
										) : (
											<span className="font-medium text-green-600 dark:text-green-400">
												เวอร์ชั่นเริ่มต้น
											</span>
										)}
									</p>
								</div>
								<FormControl>
									<Switch
										id="jokerHard"
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
							</div>
						</FormItem>
					)}
				/>

				<FormField
					control={control}
					name={getFieldName('jiliHard') as any}
					render={({ field }) => (
						<FormItem>
							<div className="flex items-center justify-between space-x-4 rounded-lg border p-4">
								<div className="flex-1 space-y-1">
									<Label
										htmlFor="jiliHard"
										className="cursor-pointer text-base font-semibold"
									>
										JILI
									</Label>
									<p className="text-sm text-muted-foreground">
										{field.value ? (
											<span className="font-medium text-blue-600 dark:text-blue-400">
												ปรับแตก ({PROVIDER_PAIRS['JILI']})
											</span>
										) : (
											<span className="font-medium text-green-600 dark:text-green-400">
												เวอร์ชั่นเริ่มต้น
											</span>
										)}
									</p>
								</div>
								<FormControl>
									<Switch
										id="jiliHard"
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
							</div>
						</FormItem>
					)}
				/>

				<FormField
					control={control}
					name={getFieldName('ppHard') as any}
					render={({ field }) => (
						<FormItem>
							<div className="flex items-center justify-between space-x-4 rounded-lg border p-4">
								<div className="flex-1 space-y-1">
									<Label
										htmlFor="ppHard"
										className="cursor-pointer text-base font-semibold"
									>
										PP
									</Label>
									<p className="text-sm text-muted-foreground">
										{field.value ? (
											<span className="font-medium text-blue-600 dark:text-blue-400">
												ปรับแตก ({PROVIDER_PAIRS['PP']})
											</span>
										) : (
											<span className="font-medium text-green-600 dark:text-green-400">
												เวอร์ชั่นเริ่มต้น
											</span>
										)}
									</p>
								</div>
								<FormControl>
									<Switch
										id="ppHard"
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
							</div>
						</FormItem>
					)}
				/>

				<FormField
					control={control}
					name={getFieldName('nlcHard') as any}
					render={({ field }) => (
						<FormItem>
							<div className="flex items-center justify-between space-x-4 rounded-lg border p-4">
								<div className="flex-1 space-y-1">
									<Label
										htmlFor="nlcHard"
										className="cursor-pointer text-base font-semibold"
									>
										NLC
									</Label>
									<p className="text-sm text-muted-foreground">
										{field.value ? (
											<span className="font-medium text-blue-600 dark:text-blue-400">
												ปรับแตก ({PROVIDER_PAIRS['NLC']})
											</span>
										) : (
											<span className="font-medium text-green-600 dark:text-green-400">
												เวอร์ชั่นเริ่มต้น
											</span>
										)}
									</p>
								</div>
								<FormControl>
									<Switch
										id="nlcHard"
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
							</div>
						</FormItem>
					)}
				/>
			</div>
		</div>
	)
}
